import { useEffect, useState } from 'react';
import { http } from '@/lib/api';
import { Laptop, Smartphone, MonitorSmartphone, Trash2, LogOut, ShieldCheck, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

function detectIcon(ua = '') {
  const u = (ua || '').toLowerCase();
  if (/mobile|iphone|android/.test(u)) return Smartphone;
  if (/ipad|tablet/.test(u)) return MonitorSmartphone;
  return Laptop;
}
function detectBrowser(ua = '') {
  if (/Chrome/i.test(ua)) return 'Chrome';
  if (/Firefox/i.test(ua)) return 'Firefox';
  if (/Edg/i.test(ua)) return 'Edge';
  if (/Safari/i.test(ua)) return 'Safari';
  if (/curl/i.test(ua)) return 'curl';
  return 'Browser';
}
function detectOS(ua = '') {
  if (/Windows/i.test(ua)) return 'Windows';
  if (/Mac OS X/i.test(ua)) return 'macOS';
  if (/iPhone|iOS/i.test(ua)) return 'iOS';
  if (/Android/i.test(ua)) return 'Android';
  if (/Linux/i.test(ua)) return 'Linux';
  return 'Unknown';
}
function prettyUA(ua = '') {
  if (!ua) return 'Unknown device';
  return `${detectBrowser(ua)} on ${detectOS(ua)}`;
}
function timeAgo(iso) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function Sessions() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await http.get('/auth/sessions');
      setSessions(data.sessions || []);
    } catch (e) {
      toast.error("Couldn't load your active sessions right now.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const revoke = async (jti) => {
    if (!window.confirm('Sign out this device?')) return;
    setRevoking((r) => ({ ...r, [jti]: true }));
    try {
      await http.delete(`/auth/sessions/${jti}`);
      toast.success('Session signed out.');
      await load();
    } catch (e) {
      toast.error("Couldn't sign out that session.");
    } finally { setRevoking((r) => ({ ...r, [jti]: false })); }
  };

  const revokeAll = async () => {
    if (!window.confirm('Sign out of every device including this one?')) return;
    try {
      await http.post('/auth/sessions/revoke-all');
      window.location.href = '/login';
    } catch (e) {
      toast.error("Couldn't sign out all sessions.");
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] px-5 lg:px-10 pt-6 lg:pt-10 pb-16 space-y-8" data-testid="sessions-page">
      <Link to="/app/profile" className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy transition-smooth">
        <ArrowLeft size={14}/> Back to Profile
      </Link>

      <section className="max-w-2xl">
        <p className="text-[11px] uppercase tracking-[0.24em] text-gold font-semibold flex items-center gap-1.5">
          <ShieldCheck size={12}/> Security
        </p>
        <h1 className="font-heading text-4xl lg:text-5xl mt-2 leading-tight">Active sessions</h1>
        <p className="text-navy/65 text-base lg:text-lg mt-2 leading-relaxed">
          Every device signed into Brotherhood+. Sign out anywhere you don&apos;t recognize.
        </p>
      </section>

      <section className="max-w-2xl space-y-3" data-testid="sessions-list">
        {loading ? (
          <div className="bg-white rounded-2xl p-6 card-soft text-navy/50">Loading…</div>
        ) : sessions.length === 0 ? (
          <div className="bg-white rounded-2xl p-6 card-soft text-navy/60">No active sessions.</div>
        ) : (
          sessions.map((s) => {
            const Icon = detectIcon(s.user_agent);
            return (
              <div key={s.jti} className="bg-white rounded-2xl p-5 card-soft flex items-center gap-4"
                   data-testid={`session-${s.jti}`}>
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${s.is_current ? 'bg-emerald-50 text-emerald-700' : 'bg-beige text-navy/70'}`}>
                  <Icon size={20}/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-navy truncate">{prettyUA(s.user_agent)}</p>
                    {s.is_current && (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-semibold">
                        This device
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-navy/50 mt-0.5">
                    {s.ip || 'unknown IP'} · Last used {timeAgo(s.last_used_at)}
                  </p>
                </div>
                {!s.is_current && (
                  <button
                    onClick={() => revoke(s.jti)}
                    disabled={revoking[s.jti]}
                    className="text-xs bg-red-50 text-red-700 rounded-full px-3 py-1.5 hover:bg-red-100 transition-smooth disabled:opacity-50"
                    data-testid={`session-revoke-${s.jti}`}
                  >
                    <Trash2 size={12} className="inline mr-1"/> Sign out
                  </button>
                )}
              </div>
            );
          })
        )}
      </section>

      {sessions.length > 0 && (
        <section className="max-w-2xl">
          <button
            onClick={revokeAll}
            className="w-full rounded-2xl bg-white border-2 border-red-200 hover:bg-red-50 hover:border-red-300 text-red-700 p-4 font-medium transition-smooth flex items-center justify-center gap-2"
            data-testid="sessions-revoke-all"
          >
            <LogOut size={16}/> Sign out of all devices
          </button>
          <p className="mt-2 text-[11px] text-navy/45 text-center leading-relaxed">
            You&apos;ll be signed out here too. You can sign back in at any time.
          </p>
        </section>
      )}
    </div>
  );
}
